import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/app/DataTable";
import { DocumentsPanel } from "@/components/app/DocumentsPanel";
import { useMe } from "@/components/app/MeContext";
import { NewInvoiceDialog } from "@/components/invoice/NewInvoiceDialog";
import { InvoiceSheet } from "@/components/invoice/InvoiceSheet";
import { INV_COLS } from "@/components/invoice/invoiceCols";
import { InvoiceAssetDialog } from "@/components/invoice/InvoiceAssetDialog";
import { useFetch } from "@/hooks/useFetch";

const INVOICABLE = ["approved", "in_production", "partially_dispatched", "dispatched", "delivered"];

export function InvoicesTab({ order, reload: reloadOrder }) {
  const { can } = useMe();
  const { data, reload } = useFetch(`/invoices?order_id=${order.id}`);
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(null);
  const [ver, setVer] = useState(0);
  const [assetInv, setAssetInv] = useState(null);
  const pending = (data || []).filter((i) => i.asset_prompt === "pending" && i.status === "active");
  const allowed = can("write") && INVOICABLE.includes(order.status) && !order.is_projection;
  const done = () => { reload(); reloadOrder?.(); };
  return (
    <div data-testid="lm-invoices-tab">
      <div className="mb-3 flex items-center gap-3">
        {allowed && <Button size="sm" onClick={() => setOpen(true)} data-testid="lm-invoice-new-btn"><Plus className="h-4 w-4 mr-1" />New invoice</Button>}
        <span className="text-xs text-slate-500">An LM can have several invoices; each can be shipped in one or more dispatches.</span>
      </div>
      {order.prefix === "LMR" && pending.length > 0 && can("assets.edit") && (
        <div className="mb-3 rounded border border-indigo-200 bg-indigo-50/60 px-3 py-2 text-xs flex flex-wrap items-center gap-2" data-testid="invoice-asset-pending">
          <b className="text-indigo-900">Add as assets?</b>
          {pending.map((i) => <Button key={i.id} size="sm" variant="outline" className="h-7" onClick={() => setAssetInv(i.id)} data-testid={`invoice-asset-open-${i.invoice_no}`}>{i.invoice_no}</Button>)}
        </div>
      )}
      <DataTable testid="lm-invoices-table" columns={INV_COLS} rows={data} onRowClick={(r) => setFocus(r.id)} empty="No invoices yet" />
      <div className="mt-6" data-testid="lm-invoice-files">
        <h3 className="text-sm font-semibold mb-1">Invoice files</h3>
        <p className="text-xs text-slate-500 mb-2">Upload invoices generated in your accounting software. They stay linked to {order.order_no} and can be downloaded any time. Put the invoice number in the title or pick the invoice record — the file then also opens from that invoice.</p>
        <DocumentsPanel key={ver} entityType="order" entityId={order.id} onlyType="Invoice" types={["Invoice"]} uploadLabel="Upload invoice" titlePlaceholder="Invoice no. (e.g. TS/26-27/0012)" invoices={data || []} />
      </div>
      {open && <NewInvoiceDialog presetOrder={order} onClose={() => setOpen(false)} onSaved={(inv) => { done(); setVer((v) => v + 1); if (inv?.asset_prompt === "pending" && can("assets.edit")) setAssetInv(inv.id); }} />}
      {assetInv && <InvoiceAssetDialog invoiceId={assetInv} onClose={() => setAssetInv(null)} onDone={reload} />}
      <InvoiceSheet key={focus} id={focus} onClose={() => { setFocus(null); setVer((v) => v + 1); }} onDone={() => { setFocus(null); setVer((v) => v + 1); done(); }} />
    </div>
  );
}
